import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import CompanyLogo from '../Components/CompanyLogo/CompanyLogo';

const styles = (theme) => ({
  header: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      alignItems: 'flex-end',
    },
  },
  bottom: {
    position: 'relative',
    margin: '20px 32px',
    justifyContent: 'space-between',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 'auto',
      margin: theme.spacing(1, 4, 2),
    },
  },
  '@media (min-aspect-ratio: 13/9) and (min-height: 280px) and (max-width: 825px)': {
    header: {
      display: 'none',
    },
    main: {
      padding: '32px 16px',
    },
    bottom: {
      display: 'none',
    },
  },

  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    overflowX: 'hidden',
    overflowY: 'hidden',
    '&$fallbackMode': {
      '& $main': {
        [theme.breakpoints.up('md')]: {
          width: '100%',
        },
        '@media (orientation: landscape)': {
          flexDirection: 'row',
        },
      },
    },
    // helper classes can be used on contained elements
    '& .noShrink': {
      flexShrink: 0,
    },
    '& .verticalSpacing': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    '& .md': {
      [theme.breakpoints.up('md')]: {},
    },
    '& .sm': {
      [theme.breakpoints.down('sm')]: {},
    },
  },
  fallbackMode: {},
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    overflow: 'hidden',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
    '&.fullSize': {
      width: '100%',
      height: '100%',
      paddingLeft: 0,
    },
    '&.reducedSize': {
      width: 'calc(100% - 630px)',
      marginLeft: 330,
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 330px)',
      },
    },
    '&.fullScreen': {
      width: '100%',
      height: '100%',
      marginLeft: 0,
    },
  },
  otherDesigns: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('xs')]: {
      position: 'relative',
      height: 'auto',
    },
  },
  containerTabletPortrait: {
    flexDirection: 'column',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',

    // the pointer events should be passed through to the visualization component
    pointerEvents: 'none', // important: don't remove this
  },
  productsList: {
    position: 'absolute',
    backgroundColor: '#fff',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      height: '100%',
    },
    '&.vertical': {
      float: 'left',
      height: '100%',
      width: 'auto',
      left: theme.spacing(1),
      top: theme.spacing(1),
    },
  },
  shoppingCart: {
    display: 'block',
  },
  outerContainer: {
    height: '100%',
    position: 'relative',
    flex: 1,
    display: 'flex',
    marginRight: 20,
    [theme.breakpoints.up('md')]: {
      marginBottom: 24,
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: 16,
    },
    [theme.breakpoints.down('sm')]: {
      overflow: 'hidden !important',
    },
    [theme.breakpoints.down('xs')]: {
      margin: '0px 16px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
    },
  },
  visualization: {
    // the visualization is in the background with absolute positioning
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    '&.fullScreen': {
      height: '100%',
    },
  },
  notice: {
    display: 'flex',
    position: 'absolute',
    bottom: 0,
    width: '100%',
    justifyContent: 'center',
  },
  logo: {
    display: 'none',
    position: 'absolute',
    top: theme.spacing(4),
    right: theme.spacing(1),
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
});

const Layout = (props) => {
  const {
    classes,
    renderHeader,
    renderMain,
    renderShoppingCart,
    renderInForeground,
    renderLoader,
    renderVisualization,
    displayVerticalList,
    isPortraitMode,
    fallbackMode,
    width,
    renderProductsList,
    renderOtherDesigns,
    renderNotice,
  } = props;

  const content = (renderInForeground && renderInForeground()) || (
    <>
      <div className={classes.header}>{renderHeader()}</div>
      <div className={classes.outerContainer}>
        <div className={classes.container}>
          <CompanyLogo className={classes.logo} />
          <div className={clsx(classes.outerContainer)}>
            <div
              className={`${classes.container} ${isPortraitMode ? classes.containerTabletPortrait : ''} ${
                displayVerticalList ? 'reducedSize' : 'fullSize'
              }`}
            >
              {!fallbackMode && <div className={`${classes.visualization}`}>{renderVisualization()}</div>}
              <div className={classes.main}>
                {renderMain({
                  area: 'main',
                  width,
                  classes,
                })}
              </div>
              <div className={classes.notice}>{renderNotice()}</div>
            </div>

            <div className={`${classes.productsList} ${displayVerticalList ? 'vertical' : ''}`}>
              {renderProductsList({
                area: 'products',
                width,
                classes,
                displayVerticalList,
              })}
            </div>
            {renderOtherDesigns && <div className={classes.otherDesigns}>{renderOtherDesigns()}</div>}
          </div>
        </div>
      </div>
      <div className={classes.bottom}>{renderShoppingCart()}</div>
    </>
  );
  return (
    <div className={classes.root}>
      {content}
      {renderLoader()}
    </div>
  );
};

Layout.propTypes = {
  classes: PropTypes.object.isRequired,
  renderHeader: PropTypes.func.isRequired,
  renderMain: PropTypes.func.isRequired,
  renderBottom: PropTypes.func.isRequired,
  renderLoader: PropTypes.func.isRequired,
  renderInForeground: PropTypes.func,
  renderShoppingCart: PropTypes.func,
  renderVisualization: PropTypes.func,
  displayVerticalList: PropTypes.bool,
  isPortraitMode: PropTypes.bool,
  fallbackMode: PropTypes.bool,
  width: PropTypes.string,
  renderProductsList: PropTypes.func,
  renderOtherDesigns: PropTypes.any,
  renderNotice: PropTypes.func,
};

Layout.defaultProps = {
  displayVerticalList: true,
};

export default withStyles(styles)(Layout);
